import { Button, Input, TextTooltip } from '@components'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { fetchPeriod, updatePeriod, updateBusiness, IPeriod } from '@query'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaCheck } from 'react-icons/fa'
import styled from 'styled-components'
import {
  ReviewPromptWrapper,
  ReviewPromptCard,
  ReviewPromptTextGroup,
  ReviewPromptActionGroup
} from './ReviewPromptCard'

interface PeriodFormValues {
  start_date: string
  end_date: string
}

const CONFIRM_PERMISSIONS = PermissionSet.BOOKKEEPING_ADMIN

export const FiscalPeriodReviewPrompt: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const canConfirm = usePermission(CONFIRM_PERMISSIONS)
  const canEditDates = usePermission(PermissionSet.REPORTING_EDIT)

  const periodId = business?.period_id
  const { data: period } = useQuery({
    queryKey: ['period', slug, periodId],
    queryFn: () => fetchPeriod({ slug, periodId: periodId! }),
    enabled: !!periodId && !!business?.fiscal_period_needs_review
  })

  const {
    register,
    handleSubmit,
    formState: { isValid }
  } = useForm<PeriodFormValues>({
    values: period
      ? { start_date: period.start_date, end_date: period.end_date }
      : { start_date: '', end_date: '' },
    mode: 'onChange'
  })

  const { mutateAsync: update } = useMutation({
    mutationFn: (data: Partial<IPeriod>) => updatePeriod({ slug, periodId: periodId! }, data)
  })

  const { mutateAsync: confirm } = useMutation({
    mutationFn: () => updateBusiness({ slug }, { fiscal_period_needs_review: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [slug] })
      queryClient.invalidateQueries({ queryKey: ['period', slug, periodId] })
    }
  })

  const [showSpinner, setShowSpinner] = useState(false)

  const onConfirm = handleSubmit(async (values: PeriodFormValues) => {
    if (!canConfirm) return

    setShowSpinner(true)
    const minDuration = new Promise(r => setTimeout(r, 400))

    try {
      const datesChanged =
        period && (values.start_date !== period.start_date || values.end_date !== period.end_date)

      if (datesChanged && !canEditDates) {
        toast.error(t('fiscalPeriodReview.missingEditPermissionTooltip'))
        return
      }

      if (datesChanged) {
        await update({ start_date: values.start_date, end_date: values.end_date })
      }
      await confirm()
    } catch {
      toast.error(t('general.error'))
    } finally {
      await minDuration
      setShowSpinner(false)
    }
  })

  if (!business?.fiscal_period_needs_review) {
    return <>{children}</>
  }

  const confirmButton = (
    <Button
      type="button"
      intent="primary"
      icon={<FaCheck />}
      onClick={onConfirm}
      showSpinner={showSpinner}
      disabled={!canConfirm || showSpinner || !isValid}
    >
      {t('fiscalPeriodReview.confirm')}
    </Button>
  )

  return (
    <ReviewPromptWrapper>
      <ReviewPromptCard>
        <ReviewPromptTextGroup>
          <h2>{t('fiscalPeriodReview.title')}</h2>
          <p>{t('fiscalPeriodReview.description')}</p>
        </ReviewPromptTextGroup>
        <ReviewPromptActionGroup>
          <FieldGroup>
            <Input
              type="date"
              label={t('fiscalPeriodReview.startDate')}
              disabled={!canEditDates}
              {...register('start_date', { required: true })}
            />
            <Input
              type="date"
              label={t('fiscalPeriodReview.endDate')}
              disabled={!canEditDates}
              {...register('end_date', { required: true })}
            />
          </FieldGroup>
          {canConfirm ? (
            confirmButton
          ) : (
            <TextTooltip tooltip={t('fiscalPeriodReview.missingPermissionTooltip')}>
              <DisabledConfirmWrapper>{confirmButton}</DisabledConfirmWrapper>
            </TextTooltip>
          )}
        </ReviewPromptActionGroup>
      </ReviewPromptCard>
    </ReviewPromptWrapper>
  )
}

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  width: 100%;
`

const DisabledConfirmWrapper = styled.span`
  display: inline-flex;
  cursor: not-allowed;
`
